import {
  dehydrate,
  HydrationBoundary,
  QueryClient,
} from "@tanstack/react-query"
import type { Metadata } from "next"
import { notFound } from "next/navigation"
import { Suspense } from "react"
import { StoreCategoriesSidebar } from "@/components/e-commerce/store/categories-sidebar"
import { StoreCategoriesSidebarSkeleton } from "@/components/e-commerce/store/categories-sidebar/skeleton"
import {
  hasAnyStoreQueryParam,
  normalizeStoreSearchParams,
  type StoreSearchParamsInput,
  type StoreSortBy,
  type StoreSortOrder,
} from "@/components/e-commerce/store/query-params/contract"
import {
  buildStoreProductsQuerySeed,
  getStoreProductsInfiniteQueryOptions,
} from "@/components/e-commerce/store/tabs/query"
import { StoreToolbar } from "@/components/e-commerce/store/toolbar"
import { env } from "@/env"
import { getStoreBySlug, getStoreProductsPage } from "@/services/dokan-service"
import type { WooCommerceProduct } from "@/services/woo-commerce-service"
import { StoreHeaderInfo } from "~/components/e-commerce/store/header-info"
import { StoreTabs } from "~/components/e-commerce/store/tabs"

interface StorePageProps {
  params: Promise<{ slug: string }>
  searchParams: Promise<StoreSearchParamsInput>
}

const buildStoreCanonicalPath = (slug: string): string => `/lojas/${slug}`

export async function generateMetadata({
  params,
  searchParams,
}: StorePageProps): Promise<Metadata> {
  const [{ slug }, resolvedSearchParams] = await Promise.all([
    params,
    searchParams,
  ])

  const store = await getStoreBySlug(slug)

  const siteUrl = env.NEXT_PUBLIC_SITE_URL ?? "http://localhost:3000"
  const canonicalUrl = `${siteUrl}${buildStoreCanonicalPath(slug)}`
  const hasQueryParams = hasAnyStoreQueryParam(resolvedSearchParams)

  if (!store) {
    return {
      metadataBase: new URL(siteUrl),
      title: "Loja nao encontrada",
      description:
        "A loja solicitada nao foi encontrada. Explore outras lojas disponiveis.",
      alternates: {
        canonical: canonicalUrl,
      },
      robots: {
        index: false,
        follow: true,
      },
    }
  }

  return {
    metadataBase: new URL(siteUrl),
    title: `${store.name} | Marketplace Triibo`,
    description: `Confira os produtos da loja ${store.name} no Marketplace Triibo.`,
    alternates: {
      canonical: canonicalUrl,
    },
    robots: hasQueryParams
      ? {
          index: false,
          follow: true,
        }
      : {
          index: true,
          follow: true,
        },
  }
}

export default async function Page({
  params,
  searchParams,
}: StorePageProps): Promise<React.ReactNode> {
  const [{ slug }, resolvedSearchParams] = await Promise.all([
    params,
    searchParams,
  ])
  const store = await getStoreBySlug(slug)

  if (!store) {
    notFound()
  }

  const normalizedQuery = normalizeStoreSearchParams(resolvedSearchParams)

  const loadStoreProductsPage = async ({
    page,
    q,
    orderby,
    order,
    per_page,
  }: {
    page: number
    q?: string
    orderby: StoreSortBy
    order: StoreSortOrder
    per_page: number
  }): Promise<{
    items: WooCommerceProduct[]
    page: number
    totalPages: number
    totalProducts: number
  }> => {
    "use server"

    const payload = await getStoreProductsPage({
      storeId: store.id,
      page,
      per_page,
      q,
      orderby,
      order,
    })

    return {
      items: payload.products,
      page,
      totalPages: payload.totalPages,
      totalProducts: payload.totalProducts,
    }
  }

  const storeProductsQueryKey = buildStoreProductsQuerySeed({
    storeId: store.id,
    storeSlug: store.slug,
    query: normalizedQuery,
  })

  const queryClient = new QueryClient()

  await queryClient.prefetchInfiniteQuery(
    getStoreProductsInfiniteQueryOptions({
      onLoadMoreProducts: loadStoreProductsPage,
      query: normalizedQuery,
      queryKeySeed: storeProductsQueryKey,
    })
  )

  return (
    <main className='mx-auto w-full p-6'>
      <div className='mx-auto grid w-full max-w-400 grid-cols-1 gap-6 lg:grid-cols-[18rem_minmax(0,1fr)]'>
        <Suspense fallback={<StoreCategoriesSidebarSkeleton />}>
          <StoreCategoriesSidebar storeId={store.id} />
        </Suspense>

        <section className='space-y-4'>
          <StoreHeaderInfo store={store} />

          <StoreToolbar shopUrl={store.shopUrl} />

          <HydrationBoundary state={dehydrate(queryClient)}>
            <StoreTabs
              onLoadMoreProducts={loadStoreProductsPage}
              storeId={store.id}
              storeSlug={store.slug}
            />
          </HydrationBoundary>
        </section>
      </div>
    </main>
  )
}
